import { t } from '@/lib/i18n'
import {
  beer_path,
  club_path,
  commercial_information_path,
  destroy_user_session_path,
  members_billing_informations_path,
  members_mypage_path,
  members_shipments_path,
  members_user_informations_path,
  neko_pages_campaings_path,
  new_us_user_path,
  new_user_session_path,
  onsen_path,
  privacy_policy_path,
  products_path,
  reservation_path,
  restaurants_path,
  root_path,
  tasting_room_path,
  team_path,
  terms_of_use_path,
  us_users_sign_in_with_phone_path,
} from '@/routes'
import type { Region } from '@/types'


export type NavLink = {
  label: string
  href: string
  /** 外部サイト・フルリロードが必要なリンク（Inertia の Link ではなく <a>） */
  external?: boolean
  newTab?: boolean
  /** このパス（またはその下）を表示しているときにハイライトする */
  activeWhen?: string[]
}

export type MemberMenuItem = NavLink & { icon: string; short: string }

/** 現在の URL が path（またはその下の階層）か */
export function isCurrent(url: string, paths: string[] | undefined): boolean {
  if (!paths) return false
  const current = url.split(/[?#]/)[0] ?? ''
  return paths.some((path) => (path === '/' ? current === '/' : current === path || current.startsWith(`${path}/`)))
}

// ナビ（従来の ERB の layout の日本・米国サイトと同じ項目）
export function navigation(region: Region, signedIn: boolean) {
  const account: NavLink = signedIn
    ? { label: t('layouts.application.club_member'), href: members_mypage_path(), activeWhen: ['/members'] }
    : region === 'jp'
      ? {
          label: t('layouts.application.login_or_registration'),
          href: new_user_session_path({ redirect_to: members_mypage_path() }),
          activeWhen: ['/users/sign_in'],
        }
      : { label: t('layouts.application.login_or_registration'), href: us_users_sign_in_with_phone_path() }

  if (region === 'jp') {
    return {
      dropdown: {
        label: 'ワイナリー',
        activeWhen: ['/'],
        items: [
          { label: t('layouts.application.about'), href: root_path() },
          { label: t('layouts.application.tasting_room'), href: tasting_room_path() },
          { label: t('layouts.application.zen_zin_onsen'), href: onsen_path() },
          { label: t('layouts.application.harvest'), href: beer_path() },
          { label: 'SUN"P"ET CELLARS (猫)', href: neko_pages_campaings_path() },
          { label: t('layouts.application.official_blog'), href: 'https://note.com/sunset_cellars/', external: true, newTab: true },
        ] as NavLink[],
      },
      links: [
        { label: t('layouts.application.team'), href: team_path(), activeWhen: ['/team'] },
        { label: t('layouts.application.shops'), href: products_path(), activeWhen: ['/products'] },
        { label: t('layouts.application.restaurant'), href: restaurants_path(), activeWhen: ['/restaurants'] },
        { label: t('layouts.application.club'), href: club_path(), activeWhen: ['/club'] },
        { label: t('layouts.application.reservation'), href: reservation_path(), external: true, newTab: true },
        account,
      ] as NavLink[],
    }
  }
  return {
    dropdown: {
      label: 'About us',
      activeWhen: ['/team'],
      items: [
        { label: t('layouts.application.team'), href: team_path() },
        { label: 'SUNPET CELLARS CATIO', href: neko_pages_campaings_path() },
      ] as NavLink[],
    },
    links: [
      { label: "Today's Menu", href: 'https://menu.sunsetcellars.com/', external: true, newTab: true },
      { label: t('layouts.application.shop'), href: 'https://suisunvalleywinecoop.square.site/', external: true, newTab: true },
      {
        label: t('layouts.application.reservation'),
        href: 'https://squareup.com/appointments/book/ljjb7zelgciayb/8PS1CEEJ0Y872/services',
        external: true,
        newTab: true,
      },
      { label: t('layouts.application.wine_club'), href: new_us_user_path() },
      { label: t('layouts.application.contact'), href: '/#contact' },
      account,
    ] as NavLink[],
  }
}

// 会員メニュー（サイドメニューとスマートフォンの下部メニュー）
export function memberMenu(region: Region): MemberMenuItem[] {
  return [
    { icon: 'fas fa-home', label: t('layouts.application.mypage'), short: t('layouts.application.mypage'), href: members_mypage_path(), activeWhen: ['/members/mypage'] },
    { icon: 'fas fa-dolly-flatbed', label: t('layouts.application.history'), short: t('layouts.application.delivery'), href: members_shipments_path(), activeWhen: ['/members/shipments'] },
    ...(region === 'jp'
      ? [{ icon: 'fas fa-shopping-cart', label: t('layouts.application.online_order'), short: t('layouts.application.order'), href: products_path(), activeWhen: ['/products', '/members/orders'] }]
      : []),
    { icon: 'far fa-address-card', label: t('layouts.application.user_information'), short: t('layouts.application.user_information'), href: members_user_informations_path(), activeWhen: ['/members/user_informations', '/users/edit'] },
    { icon: 'far fa-credit-card', label: t('layouts.application.payment_setting'), short: t('layouts.application.payment_setting'), href: members_billing_informations_path(), activeWhen: ['/members/billing_informations'] },
  ]
}

export function footerLinks(region: Region, signedIn: boolean): NavLink[] {
  if (region !== 'jp') return []
  return [
    { label: t('layouts.application.terms_of_use'), href: terms_of_use_path(), newTab: true },
    { label: t('layouts.application.privacy_policy'), href: privacy_policy_path(), newTab: true },
    { label: t('layouts.application.commercial_information_jp'), href: commercial_information_path(), newTab: true },
    ...(signedIn ? [{ label: t('layouts.application.logout'), href: destroy_user_session_path(), external: true }] : []),
  ]
}

export const logoutPath = destroy_user_session_path
